import { AlertTriangle, Info, LoaderCircle, X, type LucideIcon } from "lucide-react";
import { type FormEvent, type ReactNode, useId, useState } from "react";
import { createPortal } from "react-dom";

import { useDismiss } from "@/shared/hooks/useDismiss";

/** `info` é a confirmação comum; `warning`, a que segue com ressalva;
 *  `danger`, a que apaga ou desfaz. Muda a cor do ícone e o padrão dele. */
export type ConfirmDialogTone = "info" | "warning" | "danger";

const DEFAULT_ICON: Record<ConfirmDialogTone, LucideIcon> = {
  info: Info,
  warning: AlertTriangle,
  danger: AlertTriangle
};

/** "Fica de fora: …" e afins — a lista que antes vinha num parágrafo
 *  corrido, em destaque abaixo do texto. */
export type ConfirmDialogDetails = { title: string; items: string[] };

/**
 * A moldura de todo pop-up de confirmação (2026-10-27, pedido do
 * Alexsander: o mesmo desenho do aviso de alteração não salva). Ícone à
 * esquerda, título e texto com respiro, o X no canto e o rodapé separado
 * por linha com os botões à direita; em tela estreita os botões empilham.
 *
 * É um `<form>`: Enter confirma. Quem tem fases (o "Enviar para o
 * Dashboard", que espera o cálculo) monta os próprios botões em `actions`;
 * o caso comum usa o `ConfirmDialog` logo abaixo.
 */
export function ConfirmDialogFrame({
  title,
  tone = "info",
  icon,
  closeDisabled = false,
  onClose,
  onSubmit,
  children,
  actions
}: {
  title: string;
  tone?: ConfirmDialogTone;
  icon?: LucideIcon;
  /** Durante uma escrita que não pode ser abandonada no meio. */
  closeDisabled?: boolean;
  onClose: () => void;
  onSubmit: () => void;
  children: ReactNode;
  actions: ReactNode;
}) {
  const titleId = useId();
  const Icon = icon ?? DEFAULT_ICON[tone];
  useDismiss(!closeDisabled, onClose);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSubmit();
  };

  return createPortal(
    <>
      <div className="confirm-dialog-scrim" onClick={closeDisabled ? undefined : onClose}/>
      <div className="confirm-dialog-anchor">
        <form
          className="confirm-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          onSubmit={submit}
        >
          <button
            type="button"
            className="confirm-dialog-close"
            onClick={onClose}
            disabled={closeDisabled}
            aria-label="Fechar"
          >
            <X size={16}/>
          </button>
          <div className="confirm-dialog-body">
            <span className={`confirm-dialog-icon ${tone}`} aria-hidden="true">
              <Icon size={20}/>
            </span>
            <div className="confirm-dialog-copy">
              <h3 id={titleId}>{title}</h3>
              {children}
            </div>
          </div>
          <div className="confirm-dialog-actions">{actions}</div>
        </form>
      </div>
    </>,
    document.body
  );
}

/** A lista em destaque dentro da moldura ("Fica de fora", por exemplo). */
export function ConfirmDialogDetailsBox({ details }: { details: ConfirmDialogDetails }) {
  if (details.items.length === 0) return null;
  return (
    <div className="confirm-dialog-details">
      <strong>{details.title}</strong>
      <ul>
        {details.items.map(item => <li key={item}>{item}</li>)}
      </ul>
    </div>
  );
}

type ConfirmDialogProps = {
  title: string;
  description: string;
  confirmLabel: string;
  danger?: boolean;
  /** Ressalva sem ser destrutivo: ícone de aviso em amarelo. */
  tone?: ConfirmDialogTone;
  icon?: LucideIcon;
  details?: ConfirmDialogDetails;
  /** When set, requires this field to be filled before the confirm button is enabled.
   *  `defaultValue` pré-preenche (o diálogo desmonta ao fechar, então
   *  reabrir traz a sugestão de novo); `maxLength` corta na digitação,
   *  pra o limite do servidor ser rede de segurança e não o caminho
   *  normal de descobrir que o nome é longo demais. */
  requireField?: {
    label: string;
    type: "password" | "text";
    placeholder?: string;
    defaultValue?: string;
    maxLength?: number;
  };
  loading?: boolean;
  error?: string | null;
  onConfirm: (fieldValue: string) => void;
  onCancel: () => void;
};

export function ConfirmDialog({
  title,
  description,
  confirmLabel,
  danger = false,
  tone,
  icon,
  details,
  requireField,
  loading = false,
  error,
  onConfirm,
  onCancel
}: ConfirmDialogProps) {
  const [fieldValue, setFieldValue] = useState(requireField?.defaultValue ?? "");

  const disabled = loading || (Boolean(requireField) && fieldValue.trim().length === 0);

  return (
    <ConfirmDialogFrame
      title={title}
      tone={tone ?? (danger ? "danger" : "info")}
      icon={icon}
      onClose={onCancel}
      onSubmit={() => onConfirm(fieldValue.trim())}
      actions={
        <>
          <button type="button" className="secondary-button" onClick={onCancel} disabled={loading}>
            Cancelar
          </button>
          <button
            type="submit"
            className={danger ? "primary-button danger-button" : "primary-button"}
            disabled={disabled}
          >
            {loading ? <LoaderCircle className="spin" size={16}/> : confirmLabel}
          </button>
        </>
      }
    >
      <p>{description}</p>
      {details && <ConfirmDialogDetailsBox details={details}/>}
      {requireField && (
        <label className="account-field">
          <span>{requireField.label}</span>
          <input
            type={requireField.type}
            autoFocus
            value={fieldValue}
            onChange={event => setFieldValue(event.target.value)}
            placeholder={requireField.placeholder}
            maxLength={requireField.maxLength}
          />
        </label>
      )}
      {error && <div className="account-feedback error" aria-live="polite">{error}</div>}
    </ConfirmDialogFrame>
  );
}
